import React, { useState, useEffect } from "react";
import { Scale, Sparkles, Loader2, Download } from "lucide-react";
import { toast } from "sonner";
import { api, aiJob } from "@/lib/api";
import { PageHeader, SectionCard, StatusPill } from "@/components/Primitives";
import DataTable from "@/components/DataTable";
import { AIOutputDisclaimer } from "@/components/Disclaimer";
import AIFeedback from "@/components/AIFeedback";
import ShowReasoning from "@/components/ShowReasoning";
import { exportToPDF } from "@/lib/pdf";

export default function LegalIntelligence() {
  const [note, setNote] = useState("");
  const [scan, setScan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [allegation, setAllegation] = useState("");
  const [caseSum, setCaseSum] = useState("");
  const [response, setResponse] = useState(null);
  const [sol, setSol] = useState([]);
  const [contra, setContra] = useState([]);

  useEffect(() => {
    api.get("/legal/sol-tracker").then(r=>setSol(r.data));
    api.get("/legal/contradictions").then(r=>setContra(r.data));
  }, []);

  const scanNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try {
      const data = await aiJob("/legal/scan-note", { note_text: note });
      let parsed;
      try {
        let s = data.final.trim();
        if (s.startsWith("```")) { s = s.split("```",2)[1]; if (s.startsWith("json")) s = s.slice(4); s = s.split("```")[0]; }
        parsed = JSON.parse(s);
      } catch { parsed = { summary: data.final }; }
      parsed._meta = { score: data.score, reasoning: data.reasoning, verification_footer: data.verification_footer };
      setScan(parsed);
      toast.success("Note scanned for malpractice exposure");
    } catch { toast.error("Scan failed"); }
    finally { setBusy(false); }
  };

  const draftResp = async () => {
    if (!allegation || !caseSum) return;
    setBusy(true);
    try {
      const data = await aiJob("/legal/demand-response", { allegation, case_summary: caseSum });
      setResponse({ text: data.final, score: data.score, reasoning: data.reasoning, verification_footer: data.verification_footer });
      toast.success("Response drafted");
    } catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  const solCols = [
    { key: "case", label: "Case" },
    { key: "filed", label: "Filed" },
    { key: "sol_expires", label: "SOL Expires" },
    { key: "days_remaining", label: "Days", render: (r)=><span className={r.days_remaining<180?"text-red-600 font-semibold":""}>{r.days_remaining}</span> },
    { key: "status", label: "Status", render: (r)=><StatusPill status={r.status} /> },
  ];

  const contraCols = [
    { key: "patient", label: "Patient" },
    { key: "earlier_note", label: "Earlier" },
    { key: "later_note", label: "Later" },
    { key: "dates", label: "Dates" },
    { key: "severity", label: "Severity", render: (r)=><StatusPill status={r.severity} /> },
  ];

  return (
    <>
      <PageHeader eyebrow="Module 16" title="Legal Intelligence Layer"
        subtitle="Pre-sign malpractice scanning, demand-letter drafting, SOL tracking and documentation contradiction detection." testid="legal-page" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <SectionCard title="Malpractice Risk Scanner" testid="malpractice-card">
          <textarea value={note} onChange={(e)=>setNote(e.target.value)} rows={6} placeholder="Paste a clinical note before signing…"
            className="w-full px-3 py-2 bg-white border border-brand rounded-md text-sm" data-testid="legal-note" />
          <button onClick={scanNote} disabled={busy} className="mt-2 bg-brand-primary text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#35096B] inline-flex items-center gap-2 disabled:opacity-50" data-testid="scan-note-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Scale className="w-4 h-4" />} Scan for Risk
          </button>
          {scan && (
            <div className="mt-4 space-y-2">
              <div className="text-sm"><span className="eyebrow mr-2">Score</span><span className={`font-semibold ${scan.risk_level==="high"?"text-red-600":scan.risk_level==="medium"?"text-amber-600":"text-emerald-600"}`}>{scan.risk_score}/100 ({scan.risk_level})</span></div>
              {scan.flagged_phrases?.map((f,i)=>(
                <div key={i} className="bg-amber-50 border border-amber-200 rounded p-2 text-xs">
                  <div className="font-semibold text-amber-800">"{f.phrase}"</div>
                  <div className="text-amber-700 mt-0.5">{f.reason}</div>
                  <div className="text-emerald-700 mt-1">→ {f.suggested_revision}</div>
                </div>
              ))}
              {scan.summary && <div className="text-xs text-brand-muted mt-2">{scan.summary}</div>}
              {scan._meta?.verification_footer && (
                <div className="mt-3 px-3 py-2 rounded-md bg-amber-50 border border-amber-300 text-xs text-amber-900 italic" data-testid="legal-scan-verification-footer">{scan._meta.verification_footer}</div>
              )}
              <ShowReasoning reasoning={scan._meta?.reasoning} score={scan._meta?.score || scan.risk_score} testid="legal-scan-reasoning" />
              <AIFeedback module="legal-intelligence" outputType="malpractice_scan" testid="legal-scan-feedback" />
            </div>
          )}
        </SectionCard>

        <SectionCard title="Demand Letter Response" testid="demand-card"
          actions={response && <button onClick={()=>exportToPDF("Demand Letter Response", response.text)} className="text-xs px-3 py-1.5 rounded-md border border-brand hover:bg-brand-bg inline-flex items-center gap-1.5"><Download className="w-3.5 h-3.5" /> PDF</button>}>
          <input placeholder="Allegation summary…" value={allegation} onChange={(e)=>setAllegation(e.target.value)}
            className="w-full mb-2 px-3 py-2 bg-white border border-brand rounded-md text-sm" data-testid="allegation" />
          <textarea placeholder="Case context…" value={caseSum} onChange={(e)=>setCaseSum(e.target.value)} rows={4}
            className="w-full px-3 py-2 bg-white border border-brand rounded-md text-sm" data-testid="case-summary" />
          <button onClick={draftResp} disabled={busy} className="mt-2 bg-brand-teal text-white px-4 py-2 rounded-md text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50" data-testid="draft-resp-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Draft Response
          </button>
          {response && (
            <div className="mt-3">
              <AIOutputDisclaimer />
              <pre className="text-xs whitespace-pre-wrap bg-brand-bg p-3 rounded-md max-h-72 overflow-y-auto" data-testid="demand-resp-text">{response.text}</pre>
              {response.verification_footer && (
                <div className="mt-3 px-3 py-2 rounded-md bg-amber-50 border border-amber-300 text-xs text-amber-900 italic" data-testid="demand-verification-footer">{response.verification_footer}</div>
              )}
              <ShowReasoning reasoning={response.reasoning} score={response.score} testid="demand-reasoning" />
              <AIFeedback module="legal-intelligence" outputType="demand_response" testid="demand-feedback" />
            </div>
          )}
        </SectionCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard title="Statute of Limitations" testid="sol-section">
          <DataTable data={sol} columns={solCols} searchKeys={["case","status"]} testid="sol-table" />
        </SectionCard>
        <SectionCard title="Documentation Contradictions" testid="contra-section">
          <DataTable data={contra} columns={contraCols} searchKeys={["patient"]} testid="contra-table" />
        </SectionCard>
      </div>
    </>
  );
}
